import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import {
  useLoggedInGetSponsorUserDetails,
  useUpdateSponsorUserLoggedIn,
} from "../../../custom-hooks/queries";
import NotificationModal from "../../../components/NotificationModal";
import FormSkeletonLoader from "../../../components/FormSkeletonLoader";
import {
  rightsCategories,
  rightsDescriptions,
  rightsList,
  roleDefinitions,
  RIGHTS_DISPLAY_NAMES,
  ROLE_DISPLAY_NAMES,
  PERMISSION_CATEGORY_DISPLAY_NAMES,
} from "../../../utils/constants";
import { useUserRole } from "../../../utils/helperMethods";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { useUser } from "@clerk/clerk-react";

const schema = yup.object().shape({
  email: yup.string().email("Invalid email").required("Required"),
  first_name: yup.string().required("Required"),
  last_name: yup.string().required("Required"),
  office_phone_number: yup
    .string()
    .matches(
      /^[\d]{1}-[\d]{3}-[\d]{3}-[\d]{4}$/,
      "Please enter a valid office phone number (e.g. 1-234-567-8900)"
    )
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  phone_number: yup
    .string()
    .matches(
      /^[\d]{1}-[\d]{3}-[\d]{3}-[\d]{4}$/,
      "Please enter a valid mobile phone number (e.g. 1-234-567-8900)"
    )
    .nullable()
    .transform((value) => (value === "" ? null : value)),
  contact_preference: yup.string().required("Required"),
  current_password: yup.string().optional(),
  new_password: yup.string().when("current_password", {
    is: (val) => !!val, // if currentPassword has a value
    then: (schema) =>
      schema
        .required("New password is required")
        .min(8, "Password must be at least 8 characters")
        .matches(/[A-Z]/, "Must include an uppercase letter")
        .matches(/[a-z]/, "Must include a lowercase letter")
        .matches(/[0-9]/, "Must include a number")
        .matches(/[^A-Za-z0-9]/, "Must include a symbol"),
    otherwise: (schema) => schema.notRequired(),
  }),

  confirm_password: yup.string().when("new_password", {
    is: (val) => !!val, // if new_password has a value
    then: (schema) =>
      schema
        .required("Confirm password is required")
        .oneOf([yup.ref("new_password")], "Passwords must match"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const ManageSponsorUser = () => {
  // Auto-format phone number fields to 1-234-567-8900
  const formatPhoneInput = (e) => {
    let value = e.target.value.replace(/[^\d]/g, "");
    if (value.length > 11) value = value.slice(0, 11);
    let formatted = "";
    if (value.length > 0) {
      formatted += value[0];
    }
    if (value.length > 1) {
      formatted += "-";
      formatted += value.slice(1, 4);
    }
    if (value.length > 4) {
      formatted += "-";
      formatted += value.slice(4, 7);
    }
    if (value.length > 7) {
      formatted += "-";
      formatted += value.slice(7, 11);
    }
    e.target.value = formatted;
  };
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Add this line

  const { data: userDetails, isLoading } =
    useLoggedInGetSponsorUserDetails("Sponsor User");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const { user } = useUser();

  const { mutate: updateUser, isSuccess } = useUpdateSponsorUserLoggedIn({
    onSuccess: () => {
      setMessage("Profile updated successfully");
      setIsError(false);
      setShow(true);
    },
    onError: (error) => {
      setMessage(
        error.response?.data?.message ||
          "Failed to update profile. Please try again."
      );
      setIsError(true);
      setShow(true);
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      contact_preference: "Mobile_Phone",
    },
  });

  useEffect(() => {
    if (watch("confirm_password")?.length > 0) {
      setPasswordsMatch(watch("new_password") === watch("confirm_password"));
    } else {
      setPasswordsMatch(true);
    }
  }, [watch("new_password"), watch("confirm_password")]);

  // Update form when user details are loaded
  useEffect(() => {
    if (userDetails) {
      // Set basic user details
      setValue("email", userDetails.email);
      setValue("first_name", userDetails.first_name);
      setValue("last_name", userDetails.last_name);
      setValue("phone_number", userDetails.phone_number || "");
      setValue("office_phone_number", userDetails.office_phone_number || "");
      setValue(
        "contact_preference",
        userDetails.contact_preference || "Mobile_Phone"
      );

      // Include any additional fields from userDetails
      if (userDetails.sponsor_name) {
        setValue("sponsor_name", userDetails.sponsor_name);
      }
    }
  }, [userDetails, setValue]);

  const onSubmit = async (data) => {
    // Set submitting state to true

    // Show immediate feedback that submission is in progress

    // Only keep non-null values
    const cleanedData = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value != null)
    );

    if (data.current_password && data?.new_password) {
      await user
        ?.updatePassword({
          currentPassword: data.current_password,
          newPassword: data.new_password,
          signOutOfOtherSessions: true,
        })
        .then(() => {
          setMessage("Password updated successfully");
          setIsError(false);
          setShow(true);
        })
        .catch((error) => {
          setMessage(
            error.response?.data?.message ||
              "Failed to update password. Please try again."
          );
          setIsError(true);
          setShow(true);
        });
    }

    updateUser(cleanedData, {
      onSuccess: () => {
        setMessage("Profile Updated Successfully");
        setIsError(false);
        setShow(true);
      },
      onError: (error) => {
        setMessage(
          error.response?.data?.message ||
            "Failed to update profile. Please try again."
        );
        setIsError(true);
        setShow(true);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="w-full flex justify-center items-center min-h-[60vh]">
        <div className="animate-pulse text-center">
          <p className="text-gray-500">
            <FormSkeletonLoader />
          </p>
        </div>
      </div>
    );
  }

  // Map rights and subroles from userDetails
  const userRights = userDetails?.rights || [];
  const userSubRoles = userDetails?.sub_role || [];

  return (
    <div className="w-full px-5">
      <div className="mt-6 pb-5 space-y-3 border-b border-gray-200 px-0 md:px-1">
        <h2 className="text-lg font-semibold">My Profile</h2>
        <p className="text-sm text-gray-600">
          Update your personal information below.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        {/* Email */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Email
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              {...register("email")}
              type="email"
              className="ring-0 border-gray-300 bg-gray-100 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px] cursor-not-allowed"
              disabled={true}
            />
            {errors.email && (
              <p className="mt-2 text-sm text-red-600">
                {errors.email.message}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Email address cannot be changed
            </p>
          </div>
        </div>

        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          {/* <>
            <input
              type="password"
              style={{ display: "none" }}
              name="hidden-password"
              autoComplete="new-password"
            />
          </> */}
          <>
            <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
              Current Password
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0 relative w-[300px] lg:w-[400px] xl:w-[500px]">
              <input
                {...register("current_password")}
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                name="current_password"
                placeholder="Enter your current password"
                className="ring-0 border border-gray-300 rounded-md text-sm w-full pr-10 bg-white"
              />
              <div
                className="absolute inset-y-0 right-2 flex items-center cursor-pointer"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-500" />
                )}
              </div>
              {errors.current_password && (
                <p className="mt-2 text-sm text-red-600">
                  {errors.current_password.message}
                </p>
              )}
            </div>
          </>
          <>
            <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
              New Password
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0 relative w-[300px] lg:w-[400px] xl:w-[500px]">
              <input
                {...register("new_password")}
                type={showNewPassword ? "text" : "password"}
                autoComplete="new-password"
                name="new_password"
                placeholder="Must include uppercase, lowercase, number & symbol (min 8 characters)"
                className="ring-0 border border-gray-300 rounded-md text-sm w-full pr-10"
              />
              {/* Use a flex container to keep the icon always vertically centered relative to the input, not affected by error message */}
              <div
                className="absolute inset-y-0 right-2 flex items-center pointer-events-auto"
                style={{ height: "2.5rem" }}
              >
                <span
                  className="flex items-center cursor-pointer h-full"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  tabIndex={0}
                  role="button"
                  aria-label={
                    showNewPassword ? "Hide password" : "Show password"
                  }
                >
                  {showNewPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-500" />
                  )}
                </span>
              </div>
              {errors.new_password && (
                <p className="mt-2 text-sm text-red-600">
                  {errors.new_password.message}
                </p>
              )}
            </div>
          </>
          <>
            <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
              Confirm Password
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0 relative w-[300px] lg:w-[400px] xl:w-[500px]">
              <input
                {...register("confirm_password")}
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="new-password"
                name="confirm_password"
                placeholder="Confirm your password"
                className="ring-0 border border-gray-300 rounded-md text-sm w-full pr-10"
              />
              <div
                className="absolute inset-y-0 right-2 flex items-center pointer-events-auto"
                style={{ height: "2.5rem" }}
              >
                <span
                  className="flex items-center cursor-pointer h-full"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  tabIndex={0}
                  role="button"
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-500" />
                  )}
                </span>
              </div>
              {errors.confirm_password && (
                <p className="mt-2 text-sm text-red-600">
                  {errors.confirm_password.message}
                </p>
              )}
            </div>
          </>
        </div>

        {/* Organization (read-only if present) */}
        {userDetails?.sponsor_name && (
          <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
            <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
              Organization
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0">
              <input
                type="text"
                value={userDetails.sponsor_name}
                className="ring-0 border-gray-300 bg-gray-100 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px] cursor-not-allowed"
                disabled
              />
            </div>
          </div>
        )}

        {/* First Name */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            First Name
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              {...register("first_name")}
              type="text"
              className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px]"
            />
            {errors.first_name && (
              <p className="mt-2 text-sm text-red-600">
                {errors.first_name.message}
              </p>
            )}
          </div>
        </div>

        {/* Last Name */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Last Name
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              {...register("last_name")}
              type="text"
              className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px]"
            />
            {errors.last_name && (
              <p className="mt-2 text-sm text-red-600">
                {errors.last_name.message}
              </p>
            )}
          </div>
        </div>

        {/* Office Phone Number */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Office Phone Number
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              {...register("office_phone_number")}
              type="tel"
              className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px]"
              placeholder="e.g. 1-234-567-8900"
              maxLength={14}
              onInput={formatPhoneInput}
            />
            {errors.office_phone_number && (
              <p className="mt-2 text-sm text-red-600">
                {errors.office_phone_number.message}
              </p>
            )}
          </div>
        </div>

        {/* Phone Number */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Phone Number
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <input
              {...register("phone_number")}
              type="tel"
              className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px]"
              placeholder="e.g. 1-234-567-8900"
              maxLength={14}
              onInput={formatPhoneInput}
            />
          </div>
        </div>

        {/* Contact Preference */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Contact Preference
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <select
              {...register("contact_preference")}
              className="ring-0 border-gray-300 rounded-md text-sm w-[300px] lg:w-[400px] xl:w-[500px] [&>option]:bg-white [&>option:hover]:bg-indigo-600 [&>option:hover]:text-white"
            >
              <option value="Mobile_Phone">Mobile Phone</option>
              <option value="Office_Phone">Office Phone</option>
            </select>
          </div>
        </div>

        {/* Roles section - read-only in user profile */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Roles
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <fieldset className="space-y-4 rounded-md bg-white w-[300px] lg:w-[400px] xl:w-[500px]">
              {Object.keys(roleDefinitions).map((role) => (
                <div key={role} className="">
                  <div
                    className={`flex items-center p-4 ${
                      !userSubRoles.includes(role) ? "opacity-50" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      id={`role-${role}`}
                      checked={userSubRoles.includes(role)}
                      disabled={true}
                      readOnly
                      className="h-4 w-4 rounded border-gray-300 text-gray-300 cursor-not-allowed"
                    />
                    <label htmlFor={`role-${role}`} className="ml-3 flex-1">
                      <span className="block text-sm font-medium text-gray-900">
                        {ROLE_DISPLAY_NAMES[role] || role}
                      </span>
                      <span className="block text-sm text-gray-500 mt-1">
                        {roleDefinitions[role]?.description ||
                          `${ROLE_DISPLAY_NAMES[role] || role} role in the system`}
                      </span>
                    </label>
                  </div>
                </div>
              ))}
            </fieldset>
            <p className="mt-1 text-xs text-gray-500">
              Roles can only be changed by an administrator
            </p>
          </div>
        </div>

        {/* Permissions section - read-only */}
        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6 mt-4 border-b border-gray-300 py-5 lg:py-6">
          <label className="block text-sm font-medium text-gray-900 sm:pt-1.5">
            Permissions
          </label>
          <div className="mt-2 sm:col-span-2 sm:mt-0">
            <div className="space-y-6 w-[300px] lg:w-[400px] xl:w-[500px]">
              {Object.entries(rightsCategories).map(
                ([category, categoryData]) => (
                  <div key={category} className="rounded-md">
                    <div className="px-4 py-3">
                      <h3 className="text-sm font-medium text-gray-900">
                        {PERMISSION_CATEGORY_DISPLAY_NAMES[category] || category}
                      </h3>
                    </div>
                    <div className="px-4 py-3">
                      <div className="space-y-4">
                        {categoryData?.rights
                          ?.filter(
                            (rightItem) =>
                              rightItem !== "Trial Experience Dashboard"
                          )
                          .map((rightItem) => (
                            <div key={rightItem} className="flex items-start">
                              <div className="flex items-center h-5 pt-1">
                                <input
                                  type="checkbox"
                                  id={`right-${rightItem}`}
                                  checked={userRights.includes(rightItem)}
                                  disabled={true}
                                  readOnly
                                  className="h-4 w-4 rounded border-gray-300 text-gray-300 cursor-not-allowed"
                                />
                              </div>
                              <div
                                className={`ml-3 text-sm ${
                                  !userRights.includes(rightItem)
                                    ? "opacity-50"
                                    : ""
                                }`}
                              >
                                <label
                                  htmlFor={`right-${rightItem}`}
                                  className={`font-medium text-gray-700`}
                                >
                                  {RIGHTS_DISPLAY_NAMES[rightItem] || rightItem}
                                </label>
                                <p className="text-xs text-gray-500 mt-1">
                                  {rightsDescriptions[rightItem] ||
                                    "No description available"}
                                </p>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Permissions are determined by your roles and can only be changed
              by an administrator
            </p>
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-10 flex items-center justify-end gap-x-6 py-2 px-5">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm bg-indigo-600 hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-indigo-400 disabled:cursor-not-allowed"
          >
            Update
          </button>
        </div>
      </form>

      {/* Update the NotificationModal component to have auto-dismiss for success */}
      <NotificationModal
        open={show}
        setOpen={setShow}
        desc={message}
        title={isError ? "Error" : "Success"}
        error={isError}
      />
    </div>
  );
};

export default ManageSponsorUser;
